import { useCallback, useEffect, useState } from "react";
import DataTable, { Column, FilterOption } from "../../components/commons/DataTable";
import TicketAction from "../../components/partials/ticket/TicketAction";
import type { TicketListItem } from "../../services/webServices/ticket/types";
import { getTickets } from "../../services/webServices/ticket/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const filterOptions: FilterOption[] = [
  { value: "1", label: "پاسخ داده شده" },
  { value: "2", label: "در انتظار پاسخ" },
];

export default function Ticket() {

  const [data, setData] = useState<TicketListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchTicket = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: Record<string, any> = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    switch (filterValue) {
      case "1":
        params.status = 1;
        break;
      case "2":
        params.status = 2;
        break;
    }

    const response = await getTickets({ params });

    if (Array.isArray(response)) {
      setData(response);

      if (response.length < PAGE_SIZE) {
        const calculatedTotal =
          (currentPage - 1) * PAGE_SIZE + response.length;
        setTotalItems(calculatedTotal);
      } else {
        setTotalItems(currentPage * PAGE_SIZE + 1);
      }
    } else {
      setData([]);
      setTotalItems(0);
    }

    if (isFirstLoad) setIsLoading(false);
    hideLoading();
  }, [currentPage, filterValue]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row: TicketListItem) => {
    window.open(`https://sepehrjou.ir/ticket-details/${row.id}`, "_blank")
  };

  const handleRenderActions = (row: TicketListItem) => {
    return (
      <TicketAction
        ticketList={row}
        onActionSuccessed={() => fetchTicket()}
      />
    );
  };

  const columns: Column<TicketListItem>[] = [
    {
      key: "title",
      header: "موضوع",
      align: "right",
      sortable: true,
      width: "35%",
      accessor: (row) => row.title,
    },
    {
      key: "user",
      header: "نام کاربر",
      align: "right",
      sortable: true,
      width: "20%",
      accessor: (row) => row.user,
    },
    {
      key: "updated_at",
      header: "آخرین تغییر",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span>
            {row.updated_at
              ? new Date(row.updated_at).toLocaleDateString("fa-IR")
              : "بدون تاریخ"}
          </span>
        </div>
      )
    },
    {
      key: "ticket_number",
      header: "شماره تیکت",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => row.ticket_number,
    }
  ];

  return (
    <DataTable
      title="لیست تیکت‌ها"
      data={data}
      columns={columns}
      loading={isLoading}
      loadingText="در حال بارگذاری تیکت‌ها..."
      emptyText="تیکتی یافت نشد"

      filterable={true}
      filterPlaceholder="همه تیکت‌ها"
      filterOptions={filterOptions}
      filterValue={filterValue}
      onFilterChange={handleFilterChange}

      pagination={true}
      serverSidePagination={true}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      totalItems={totalItems}
      pageSize={PAGE_SIZE}

      onRowClick={handleOnRowClick}
      renderActions={handleRenderActions}
    />
  );
};
